import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { SEO } from '../components/SEO';
type FAQItem = {
  id: string;
  question: string;
  answer: string;
};
type FAQSection = {
  id: string;
  title: string;
  items: FAQItem[];
};
const faqs: FAQSection[] = [{
  id: 'manufacturers',
  title: 'Manufacturers',
  items: [{
    id: 'm-access',
    question: 'How is access to Aspect granted?',
    answer: 'Manufacturer access is granted after a brief scheduling conversation. Access is reviewed to confirm usage needs, rights expectations, and system fit. Aspect is not self-serve.'
  }, {
    id: 'm-verified',
    question: 'What does "verified" mean for images in the system?',
    answer: 'Verified means that what appears in the image has been confirmed against available project and product documentation. Verification establishes presence and context, not performance, endorsement, or compliance.'
  }, {
    id: 'm-rights',
    question: 'What rights are included when I license an image?',
    answer: 'Licensed images include explicit usage rights defined at the time of access. Permitted uses, attribution requirements, and restrictions are visible before download. No implied rights are granted.'
  }, {
    id: 'm-products',
    question: 'How are products shown in images confirmed?',
    answer: 'Products are verified at the level supported by available documentation. The verification level is recorded explicitly and does not imply completeness beyond what is confirmed.'
  }, {
    id: 'm-stock',
    question: 'Is Aspect a stock photography library or a marketplace?',
    answer: 'No. Aspect is a rights and verification system for real-world architectural imagery. Images enter the system only after permissions, verification, and metadata requirements are met.'
  }, {
    id: 'm-today',
    question: 'What is available today, and what comes later?',
    answer: 'The system rules, workflows, and governance are defined today. Inventory expands as projects are approved and documented. Additional capabilities are introduced only when operational.'
  }]
}, {
  id: 'architects',
  title: 'Architects',
  items: [{
    id: 'a-obligate',
    question: 'Does submitting a project obligate participation or usage?',
    answer: 'No. Submitting a project allows Aspect to evaluate eligibility and documentation requirements. No photography or distribution occurs without architect review.'
  }, {
    id: 'a-approve',
    question: 'Who approves how images are used and distributed?',
    answer: 'Architects review and approve usage before images are distributed. Usage boundaries are defined explicitly and respected across the system.'
  }, {
    id: 'a-attribution',
    question: 'How is architectural attribution handled?',
    answer: "Attribution is preserved where applicable and recorded as part of the asset's metadata. Usage terms specify how attribution must appear when required."
  }, {
    id: 'a-withdraw',
    question: 'Can a project be withdrawn or usage restricted later?',
    answer: 'Yes. Participation is reviewable and revocable. Restrictions or takedown requests are handled through defined governance processes.'
  }, {
    id: 'a-marketing',
    question: 'Is Aspect a marketing or promotional platform?',
    answer: 'No. Aspect documents completed work and governs reuse. It does not promote firms, run campaigns, or position projects as marketing content.'
  }]
}, {
  id: 'owners',
  title: 'Owners',
  items: [{
    id: 'o-obligate',
    question: 'Does submitting a project obligate photography or distribution?',
    answer: 'No. Submitting a project allows Aspect to evaluate eligibility and participation requirements. No photography or distribution occurs without explicit owner approval.'
  }, {
    id: 'o-control',
    question: 'What control do owners retain over image usage?',
    answer: 'Owners define what is photographed, how projects are referenced, and where images may appear. Usage boundaries are explicit and enforceable.'
  }, {
    id: 'o-privacy',
    question: 'How are privacy, tenants, and sensitive areas handled?',
    answer: 'Privacy considerations are addressed during project intake. Sensitive areas, occupied spaces, and restrictions are respected throughout documentation and usage.'
  }, {
    id: 'o-takedown',
    question: 'Can owners restrict or request takedown of images later?',
    answer: 'Yes. Owners may request restrictions or takedowns through defined processes. Usage remains auditable.'
  }, {
    id: 'o-approval',
    question: 'Is owner approval required for manufacturer access?',
    answer: 'Yes. Manufacturer access depends on projects approved and documented by owners and architects.'
  }]
}, {
  id: 'photographers',
  title: 'Photographers',
  items: [{
    id: 'p-marketplace',
    question: 'Is Aspect a marketplace or gig platform?',
    answer: 'No. Aspect is professional infrastructure for architectural photography, not an open marketplace or gig platform.'
  }, {
    id: 'p-scope',
    question: 'How are assignments scoped and compensated?',
    answer: 'Assignments are scoped in advance, with compensation and usage terms defined before work begins. Speculative or unpaid work is not required.'
  }, {
    id: 'p-rights',
    question: 'How are rights and attribution handled?',
    answer: 'Rights and attribution are defined contractually per assignment. Usage is governed and tracked according to agreed terms.'
  }, {
    id: 'p-guarantee',
    question: 'Is participation guaranteed after applying?',
    answer: 'No. Applications are reviewed, and participation is selective based on experience, fit, and availability.'
  }, {
    id: 'p-archive',
    question: 'Can previously captured work be included in Aspect?',
    answer: 'In some cases, yes. Previously captured work may be evaluated individually when it meets the same verification, rights, approval, and documentation requirements as commissioned photography. Archive participation is optional and selective, and inclusion is not guaranteed.'
  }]
}];
export function FAQPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const location = useLocation();
  // Handle anchor scrolling on mount/hash change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  }, [location]);
  const toggleQuestion = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  return <>
      <SEO title="Aspect — FAQs" description="Procedural answers by role covering access, rights, verification, and participation." data-id="element-126" />
      <div className="space-y-24" data-id="element-127">
        <div className="max-w-2xl" data-id="element-128">
          <h1 className="text-3xl md:text-4xl font-medium leading-tight text-aspect-black" data-id="element-129">
            Frequently Asked Questions
          </h1>
        </div>

        <div className="space-y-20" data-id="element-130">
          {faqs.map(section => <section key={section.id} id={section.id} className="scroll-mt-32" data-id="element-131">
              <div className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-8" data-id="element-132">
                <div className="md:col-span-4" data-id="element-133">
                  <h2 className="font-serif text-lg text-aspect-black sticky top-32" data-id="element-134">
                    {section.title}
                  </h2>
                </div>
                <div className="md:col-span-8" data-id="element-135">
                  <div className="divide-y divide-aspect-gray/10" data-id="element-136">
                    {section.items.map(item => <div key={item.id} className="group" data-id="element-137">
                        <button onClick={() => toggleQuestion(item.id)} className="w-full py-6 flex items-start justify-between gap-4 text-left focus:outline-none" aria-expanded={expandedId === item.id} data-id="element-138">
                          <span className={`text-base transition-colors ${expandedId === item.id ? 'text-aspect-black font-medium' : 'text-aspect-black/80 group-hover:text-aspect-black'}`} data-id="element-139">
                            {item.question}
                          </span>
                          <ChevronDown className={`h-5 w-5 text-aspect-gray flex-shrink-0 transition-transform duration-200 ${expandedId === item.id ? 'rotate-180 text-aspect-black' : ''}`} data-id="element-140" />
                        </button>

                        {expandedId === item.id && <div className="pb-8 pr-8 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-141">
                            <p className="text-aspect-gray leading-relaxed" data-id="element-142">
                              {item.answer}
                            </p>
                          </div>}
                      </div>)}
                  </div>
                </div>
              </div>
            </section>)}
        </div>
      </div>
    </>;
}